/**
 * BLRRD Text Tool — model.js  (Phase 4.G.vector, Wave 12.2)
 *
 * Text-layer data model. v1 is DESTRUCTIVE-RASTER: the model describes a
 * text block that gets rendered once via Canvas-2D `fillText` and baked
 * into a pixel layer (src/text/render.js). Editable re-rendering of placed
 * text is a Wave 12.3 follow-up.
 *
 * Privacy-first font policy (NON-NEGOTIABLE)
 * ──────────────────────────────────────────
 * BLRRD ships NO web fonts (no Google Fonts, no @font-face downloads).
 * Every text block must resolve to a SYSTEM font stack. The model
 * exposes three curated stacks (sans / serif / mono) and a strict
 * resolver that rejects any family not on the allow-list — this is what
 * keeps the "no external origins" privacy claim intact and the security
 * lint clean.
 *
 * Provenance: clean-room. fillText / measureText semantics per the W3C
 * HTML Canvas 2D Context spec §11.4. NO third-party text-shaping library.
 */

/* ── System-font stacks (the ONLY allowed families) ──────────────────── */

/**
 * Curated system-font stacks. Keys are stable tokens persisted in the
 * model; values are the CSS `font-family` strings handed to the canvas
 * context. NO downloadable fonts — every entry resolves to a font that
 * ships with the OS.
 */
export const SYSTEM_FONTS = Object.freeze({
    sans:  "system-ui, -apple-system, 'Segoe UI', sans-serif",
    serif: "Georgia, 'Times New Roman', serif",
    mono:  "'SF Mono', 'Cascadia Code', 'Consolas', monospace",
});

/** Ordered list of allowed font tokens (for UI <select> population). */
export const FONT_FAMILY_LIST = Object.freeze(['sans', 'serif', 'mono']);

/** Allowed text alignments — maps 1:1 onto canvas `textAlign`. */
export const TEXT_ALIGN_LIST = Object.freeze(['left', 'center', 'right']);

export const DEFAULT_FONT_FAMILY = 'sans';
export const DEFAULT_FONT_SIZE   = 48;
export const DEFAULT_ALIGN       = 'left';
/** Line-height as a multiple of font size (W3C "normal" ≈ 1.2). */
export const DEFAULT_LINE_HEIGHT = 1.25;

/**
 * Resolve a font-family token to its CSS stack. Rejects anything not on
 * the allow-list — falls back to the sans stack rather than honouring an
 * arbitrary (potentially web-font) family. This is the privacy gate.
 *
 * @param {string} family  one of FONT_FAMILY_LIST
 * @returns {string} CSS font-family string (system-only)
 */
export function resolveFontStack(family) {
    if (typeof family === 'string' && Object.prototype.hasOwnProperty.call(SYSTEM_FONTS, family)) {
        return SYSTEM_FONTS[family];
    }
    return SYSTEM_FONTS[DEFAULT_FONT_FAMILY];
}

/**
 * True when the supplied family token is a recognised system font. The
 * render path asserts this so a web-font family can never sneak through.
 */
export function isSystemFont(family) {
    return typeof family === 'string'
        && Object.prototype.hasOwnProperty.call(SYSTEM_FONTS, family);
}

/* ── Text-block model ────────────────────────────────────────────────── */

/**
 * Construct a text-block model. Colour is an ACEScg-linear RGBA quad (the
 * working space) — the render path converts it to display sRGB only at
 * the Canvas-2D boundary.
 *
 * @param {object} opts
 * @param {string} opts.text                 the (possibly multi-line) string
 * @param {string} [opts.fontFamily='sans']  one of FONT_FAMILY_LIST
 * @param {number} [opts.size=48]            font size in image pixels
 * @param {number[]} [opts.color]            ACEScg RGBA; default opaque white
 * @param {string} [opts.align='left']       one of TEXT_ALIGN_LIST
 * @param {number} [opts.x=0]                anchor x in image pixels
 * @param {number} [opts.y=0]                anchor y (top of first line)
 * @param {number} [opts.lineHeight=1.25]    multiplier of font size
 * @returns {object} text-block model
 */
export function createTextBlock(opts) {
    if (!opts || typeof opts !== 'object') {
        throw new Error('createTextBlock: opts required');
    }
    const text = typeof opts.text === 'string' ? opts.text : '';
    const fontFamily = isSystemFont(opts.fontFamily) ? opts.fontFamily : DEFAULT_FONT_FAMILY;
    const size = Number.isFinite(opts.size) && opts.size > 0 ? opts.size : DEFAULT_FONT_SIZE;
    const align = TEXT_ALIGN_LIST.includes(opts.align) ? opts.align : DEFAULT_ALIGN;
    const x = Number.isFinite(opts.x) ? opts.x : 0;
    const y = Number.isFinite(opts.y) ? opts.y : 0;
    const lineHeight = Number.isFinite(opts.lineHeight) && opts.lineHeight > 0
        ? opts.lineHeight : DEFAULT_LINE_HEIGHT;
    let color = [1, 1, 1, 1];
    if (Array.isArray(opts.color) || ArrayBuffer.isView(opts.color)) {
        color = [
            Number(opts.color[0]) || 0,
            Number(opts.color[1]) || 0,
            Number(opts.color[2]) || 0,
            opts.color.length >= 4 ? (Number(opts.color[3]) || 0) : 1,
        ];
    }
    return { text, fontFamily, size, color, align, x, y, lineHeight };
}

/** Split a text block into its lines (W3C: \n is the hard line break). */
export function textLines(block) {
    return String(block.text == null ? '' : block.text).split('\n');
}

/**
 * Build the CSS `font` shorthand for a block: "<size>px <stack>". The
 * stack is resolved through the privacy gate so a non-system family is
 * impossible here.
 */
export function fontShorthand(block) {
    return `${block.size}px ${resolveFontStack(block.fontFamily)}`;
}

/** True when the block would rasterise to nothing (empty / whitespace). */
export function isEmptyTextBlock(block) {
    return !block || typeof block.text !== 'string' || block.text.trim().length === 0;
}

/** Short preview string for a layer name ("Text: Hello wo…"). */
export function textPreview(block, maxLen = 16) {
    const raw = String(block && block.text ? block.text : '').replace(/\s+/g, ' ').trim();
    if (raw.length <= maxLen) return raw;
    return raw.slice(0, maxLen - 1) + '…';
}
